/**
 * StripeProvider - Stripe Elements Provider
 * SESSION_187: Registration-time payment integration
 *
 * Wraps the application with Stripe Elements context for payment form integration.
 */

import React from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

/**
 * Load Stripe with publishable key from environment variables.
 * This creates a singleton promise that's resolved once globally.
 */
const stripePromise: Promise<Stripe | null> = loadStripe(
  import.meta.env['VITE_STRIPE_PUBLISHABLE_KEY'] as string
);

/**
 * Props for StripeProvider component.
 */
interface StripeProviderProps {
  children: React.ReactNode;
}

/**
 * StripeProvider - Wraps application with Stripe Elements context.
 *
 * Usage:
 * - Wrap your app or payment-related routes with this provider
 * - Use useStripe() and useElements() hooks in child components
 *
 * @example
 * ```tsx
 * <StripeProvider>
 *   <PaymentForm />
 * </StripeProvider>
 * ```
 */
export const StripeProvider: React.FC<StripeProviderProps> = ({
  children
}): React.ReactElement => {
  return (
    <Elements
      stripe={stripePromise}
      options={{
        locale: 'en',
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#4D18C4', // MentionedMe primary purple
            colorBackground: '#ffffff',
            colorText: '#1a1a1a',
            colorDanger: '#fa5252',
            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            borderRadius: '4px',
            spacingUnit: '4px'
          }
        }
      }}
    >
      {children}
    </Elements>
  );
};

export default StripeProvider;
